import React from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { Button } from '../ui';

// Pós-deploy, o chunk lazy antigo não existe mais no servidor. React.lazy guarda a promessa rejeitada,
// então o "Tentar de novo" re-renderiza o MESMO erro. Uma recarga por minuto busca o index.html novo.
const CHAVE = 'recarregou_por_chunk_em';
const ehChunkVelho = (e) => e?.name === 'ChunkLoadError' || /Loading (CSS )?chunk [0-9a-z_-]+ failed/i.test(String(e?.message || ''));
const recarregouHaPouco = () => {
  try { return Date.now() - Number(sessionStorage.getItem(CHAVE) || 0) < 60_000; } catch { return true; }
};

/**
 * Sem isto, qualquer exceção durante o render derrubava a aplicação inteira para uma tela branca,
 * sem mensagem e sem caminho de volta. Envolve cada rota, então a falha fica contida na tela.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { erro: null };
  }

  static getDerivedStateFromError(erro) {
    return { erro };
  }

  componentDidCatch(erro, info) {
    // Sem serviço de erro configurado ainda; o console é o registro disponível.
    console.error('[ErrorBoundary]', erro, info?.componentStack);
    if (ehChunkVelho(erro) && !recarregouHaPouco()) {
      try { sessionStorage.setItem(CHAVE, String(Date.now())); } catch { /* segue: a recarga ainda vale */ }
      window.location.reload();
    }
  }

  tentarDeNovo = () => this.setState({ erro: null });

  render() {
    if (!this.state.erro) return this.props.children;
    // Peça do shell (paleta ⌘K): falhar não pode virar tela de erro por cima da tela que funciona.
    // A recarga do chunk velho já rodou no componentDidCatch.
    if (this.props.silencioso) return null;

    if (ehChunkVelho(this.state.erro)) {
      return (
        <div className="grid min-h-[60vh] place-items-center p-6">
          <div className="max-w-md text-center">
            <h1 className="font-display text-xl font-bold text-text">O sistema foi atualizado</h1>
            <p className="mt-2 text-sm text-dim">Recarregue a página para abrir a versão nova.</p>
            <div className="mt-5 flex justify-center">
              <Button onClick={() => window.location.reload()}>
                <RotateCcw className="h-4 w-4" /> Recarregar
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="grid min-h-[60vh] place-items-center p-6">
        <div className="max-w-md text-center">
          <span className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-xl bg-risk/12 text-risk">
            <AlertTriangle className="h-6 w-6" />
          </span>
          <h1 className="font-display text-xl font-bold text-text">Esta tela travou</h1>
          <p className="mt-2 text-sm text-dim">
            O resto do sistema continua funcionando. Se acontecer de novo na mesma tela, vale
            reportar — a mensagem técnica está no console do navegador.
          </p>
          <p className="mt-3 rounded-lg bg-surface-2 px-3 py-2 text-left text-xs text-faint">
            {String(this.state.erro?.message || this.state.erro)}
          </p>
          <div className="mt-5 flex justify-center gap-2">
            <Button onClick={this.tentarDeNovo}>
              <RotateCcw className="h-4 w-4" /> Tentar de novo
            </Button>
            <Button variant="outline" onClick={() => window.location.assign('/')}>
              Ir para o Painel
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
